import { uniq } from "lodash";
import type { SagaIterator } from "redux-saga";
import { call, debounce, put, select, takeEvery, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as multiSearchApi from "@/shop/api/multiSearch";
import { getIsLoggedIn, getSelectedLanguage } from "@/shop/reducers/session/selectors";
import * as TrackerActions from "@/shop/tracker/actions";
import type { IMultiSearchResult } from "@/shop/types/multiSearch";
import { multiSearchSources } from "@/shop/types/multiSearch";

import * as MultiSearchActions from "./actions";
import { MULTISEARCH_MINIMUM_LENGTH_SEARCHTERM, MULTISEARCH_TEXT_FILTER_REGEX } from "./constants";
import { getMultiSearchTokenizedQueryTerms } from "./selectors";

function* tokenizeMultiSearchQuery(action: ActionType<typeof MultiSearchActions.tokenizeQuery>): SagaIterator {
  const searchText: string = action.payload;
  const tokenizedQuery = tokenizeSearchText(searchText);
  yield put(MultiSearchActions.setSearchText(searchText));
  yield put(MultiSearchActions.setTokenizedQuery(tokenizedQuery));
  // clear results
  for (const source of multiSearchSources) {
    yield put(MultiSearchActions.clearResults(null, { collection: source }));
  }
}

function tokenizeSearchText(searchText: string): string[] {
  return uniq(
    searchText
      .trim()
      .split(/[\s;,\u200B]+/) // whitespace, comma semicolon and zero width space
      // .map(s => s.replace(/[.]/g, "")) // Replacing the dots breaks search for customer material numbers.
      .filter((s) => s !== "" && !/[\s]+/.test(s))
  );
}

function* submitSearchSaga(_: ActionType<typeof MultiSearchActions.submitSearch>): SagaIterator {
  const isLoggedIn = (yield select(getIsLoggedIn)) as boolean;
  for (const source of multiSearchSources) {
    if (multiSearchApi.doesMultiSearchRequireLogin(source) && !isLoggedIn) {
      continue; // skip sources that require login if user is not logged in
    }
    yield put(MultiSearchActions.fetchResults(null, { collection: source }));
  }
}

function* resetSearchSaga(_: ActionType<typeof MultiSearchActions.resetSearch>): SagaIterator {
  for (const source of multiSearchSources) {
    yield put(MultiSearchActions.clearResults(null, { collection: source }));
  }
}

function* fetchResults(action: ActionType<typeof MultiSearchActions.fetchResults>): SagaIterator {
  const { collection } = action.meta;
  const queryTerms = (yield select(getMultiSearchTokenizedQueryTerms)) as string[];
  const queryTermsFiltered = queryTerms
    .filter((item) => item.length >= MULTISEARCH_MINIMUM_LENGTH_SEARCHTERM)
    .map((term) => term.replace(MULTISEARCH_TEXT_FILTER_REGEX, ""));
  const lang = (yield select(getSelectedLanguage)) as string;

  try {
    yield put(MultiSearchActions.fetchStart(null, { collection }));
    const results = (yield call(multiSearchApi.fetchMultiSearchResults, collection, queryTermsFiltered, lang)) as IMultiSearchResult;
    yield put(MultiSearchActions.fetchSuccess(results, { collection }));
    yield put(TrackerActions.trackMultiSearchResults({ searchTerms: queryTermsFiltered, searchTopic: collection, resultCount: results.count }));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(MultiSearchActions.fetchFail(err, { collection }));
  }
}

export default function* multiSearchSaga(): SagaIterator {
  yield debounce(100, getType(MultiSearchActions.tokenizeQuery), tokenizeMultiSearchQuery);
  yield takeLeading(getType(MultiSearchActions.submitSearch), submitSearchSaga);
  yield takeLeading(getType(MultiSearchActions.resetSearch), resetSearchSaga);
  yield takeEvery(getType(MultiSearchActions.fetchResults), fetchResults);
}
